import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Clock } from "lucide-react";
import { useSubscription, daysRemaining, graceDaysRemaining, writeState } from "@/hooks/useSubscription";
import { useIsStaff } from "@/hooks/useStaffRoles";

const REFRESH_MS = 60 * 60 * 1000; // recompute the countdown hourly so it changes daily

export const TrialBanner = () => {
  const { t } = useTranslation();
  const { data: sub } = useSubscription();
  const { isStaff } = useIsStaff();

  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  if (!sub || isStaff) return null;
  const days = daysRemaining(sub);

  if (sub.status === "trialing" && days !== null && days <= 5) {
    return (
      <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 text-sm flex items-center justify-center gap-3 text-amber-900 dark:text-amber-200">
        <Clock className="h-4 w-4" />
        <span>{t("subscription.trial.daysLeft", { count: days })}</span>
        <Link to="/billing/plans" className="underline font-medium">
          {t("subscription.trial.choosePlan")}
        </Link>
      </div>
    );
  }

  if (sub.status === "past_due") {
    const locked = writeState(sub) === "past_due_locked";
    const grace = graceDaysRemaining(sub);

    if (locked) {
      return (
        <div className="bg-destructive/20 border-b border-destructive/40 px-4 py-2 text-sm flex items-center justify-center gap-3 text-destructive">
          <AlertTriangle className="h-4 w-4" />
          <span>{t("subscription.pastDue.messageLocked")}</span>
          <Link to="/billing" className="underline font-medium">
            {t("subscription.pastDue.renew")}
          </Link>
        </div>
      );
    }

    return (
      <div className="bg-destructive/15 border-b border-destructive/30 px-4 py-2 text-sm flex items-center justify-center gap-3 text-destructive">
        <AlertTriangle className="h-4 w-4" />
        <span>{t("subscription.pastDue.messageDays", { count: grace ?? 0 })}</span>
        <Link to="/billing" className="underline font-medium">
          {t("subscription.pastDue.renew")}
        </Link>
      </div>
    );
  }

  return null;
};
